import type {ReactNode} from "react";
import {css} from "@panda/css";

import {
    HighlightedText,
    type ISearchComboboxOptionDomProps,
    SearchComboboxMetrics,
    type SearchComboboxMode,
    SearchComboboxOption
} from "@pg-design/search-combobox-module";
import {PRICES_SEARCH_TEST_IDS} from "@pg-mono/test-ids";

import {PRICES_SEARCH_COPY} from "../../constants/prices_search";
import type {IPricesSearchMetricValues, IPricesSearchNearbyItem, PricesSearchSectionItem} from "../../types/IPricesSearchModel";
import {formatPriceM2} from "../../utils/format_price_m2";
import {formatRcnPointPeriodShort} from "../../utils/format_rcn_period_label";
import {formatTransactionsCount} from "../../utils/format_transactions_count";
import {PricesSearchTypeIcon} from "./PricesSearchTypeIcon";

export type PricesSearchRowItem = PricesSearchSectionItem | IPricesSearchNearbyItem;

/**
 * Price, its period and transactions of a row; a missing or zero value renders no line (never "0 zł/m²"). The period
 * ("lip 2025", "III kw. 2024", "2025 r.") goes with the price of a number or an address, which can be years old.
 */
export const getPricesSearchMetricLines = (metrics: IPricesSearchMetricValues | undefined): {primary?: string; primaryHint?: string; secondary?: string} => ({
    ...(metrics?.priceM2 ? {primary: formatPriceM2(metrics.priceM2)} : {}),
    ...(metrics?.priceM2 && metrics.pricePeriod ? {primaryHint: formatRcnPointPeriodShort(metrics.pricePeriod)} : {}),
    ...(metrics?.transactionsCount ? {secondary: formatTransactionsCount(metrics.transactionsCount, metrics.transactionsIsLowerBound)} : {})
});

/** "brak danych" in the price slot of a number or an address RCN publishes no price for; the transactions stay. */
export const PricesSearchUnavailableMetrics = (props: {
    metrics: IPricesSearchMetricValues | undefined;
    layout?: "stacked" | "inline";
    alignColumns?: boolean;
}) => (
    <SearchComboboxMetrics
        layout={props.layout}
        alignColumns={props.alignColumns}
        primary={PRICES_SEARCH_COPY.noData}
        isPrimaryMuted
        secondary={getPricesSearchMetricLines(props.metrics).secondary}
    />
);

interface IProps {
    item: PricesSearchRowItem;
    /** `getOptionProps(item.id)`. */
    optionProps: ISearchComboboxOptionDomProps;
    mode: SearchComboboxMode;
    /** Replaces the metrics column (the nearby spinner). */
    meta?: ReactNode;
    /** Recent rows: "×" removes the entry. */
    onRemove?: () => void;
    /** The subtitle wraps instead of ending in an ellipsis - a message that has to be read whole. */
    isSubtitleWrapped?: boolean;
    dataTestId?: string;
}

/**
 * One suggestion row: type or history tile, highlighted title, "Typ · region", price and transactions, ↵ on
 * desktop. An address without data is muted by `aria-disabled` from the combobox and says "brak danych" for the price.
 */
export const PricesSearchRow = (props: IProps) => {
    const {item, optionProps, mode, meta, onRemove, isSubtitleWrapped, dataTestId = PRICES_SEARCH_TEST_IDS.OPTION} = props;
    const isRecent = "recentId" in item && item.recentId !== undefined;
    const {primary, primaryHint, secondary} = getPricesSearchMetricLines(item.metrics);
    const isUnavailable = item.kind === "address" && item.isUnavailable === true;
    const metrics = isUnavailable ? (
        <PricesSearchUnavailableMetrics metrics={item.metrics} />
    ) : primary || secondary ? (
        <SearchComboboxMetrics primary={primary} primaryHint={primaryHint} secondary={secondary} />
    ) : undefined;

    return (
        <SearchComboboxOption
            {...optionProps}
            icon={<PricesSearchTypeIcon kind={isRecent ? "recent" : item.kind} />}
            title={
                <>
                    <HighlightedText text={item.title} ranges={item.highlightRanges} />
                    {item.kind === "street" ? (
                        <span className={srOnlyStyle}>
                            {item.isExpanded ? PRICES_SEARCH_COPY.streetState.expanded : PRICES_SEARCH_COPY.streetState.collapsed}
                        </span>
                    ) : null}
                </>
            }
            subtitle={item.subtitle}
            isSubtitleWrapped={isSubtitleWrapped}
            meta={meta ?? metrics}
            showKbd={mode === "dropdown"}
            {...(onRemove ? {onRemove, removeLabel: PRICES_SEARCH_COPY.removeRecent(item.title), removeDataTestId: PRICES_SEARCH_TEST_IDS.RECENT_REMOVE} : {})}
            dataTestId={dataTestId}
        />
    );
};

const srOnlyStyle = css({
    position: "absolute",
    width: "1px",
    height: "1px",
    padding: "0",
    margin: "-1px",
    overflow: "hidden",
    clip: "rect(0, 0, 0, 0)",
    whiteSpace: "nowrap",
    borderWidth: "0"
});
import type {ReactNode} from "react";
import {css} from "@panda/css";

import {
    HighlightedText,
    type ISearchComboboxOptionDomProps,
    SearchComboboxMetrics,
    type SearchComboboxMode,
    SearchComboboxOption
} from "@pg-design/search-combobox-module";
import {PRICES_SEARCH_TEST_IDS} from "@pg-mono/test-ids";

import {PRICES_SEARCH_COPY} from "../../constants/prices_search";
import type {IPricesSearchMetricValues, IPricesSearchNearbyItem, PricesSearchSectionItem} from "../../types/IPricesSearchModel";
import {formatPriceM2} from "../../utils/format_price_m2";
import {formatRcnPointPeriodShort} from "../../utils/format_rcn_period_label";
import {formatTransactionsCount} from "../../utils/format_transactions_count";
import {PricesSearchTypeIcon} from "./PricesSearchTypeIcon";

export type PricesSearchRowItem = PricesSearchSectionItem | IPricesSearchNearbyItem;

/**
 * Price, its period and transactions of a row; a missing or zero value renders no line (never "0 zł/m²"). The period
 * ("lip 2025", "III kw. 2024", "2025 r.") goes with the price of a number or an address, which can be years old.
 */
export const getPricesSearchMetricLines = (metrics: IPricesSearchMetricValues | undefined): {primary?: string; primaryHint?: string; secondary?: string} => ({
    ...(metrics?.priceM2 ? {primary: formatPriceM2(metrics.priceM2)} : {}),
    ...(metrics?.priceM2 && metrics.pricePeriod ? {primaryHint: formatRcnPointPeriodShort(metrics.pricePeriod)} : {}),
    ...(metrics?.transactionsCount ? {secondary: formatTransactionsCount(metrics.transactionsCount, metrics.transactionsIsLowerBound)} : {})
});

/** "brak danych" in the price slot of a number or an address RCN publishes no price for; the transactions stay. */
export const PricesSearchUnavailableMetrics = (props: {
    metrics: IPricesSearchMetricValues | undefined;
    layout?: "stacked" | "inline";
    alignColumns?: boolean;
}) => (
    <SearchComboboxMetrics
        layout={props.layout}
        alignColumns={props.alignColumns}
        primary={PRICES_SEARCH_COPY.noData}
        isPrimaryMuted
        secondary={getPricesSearchMetricLines(props.metrics).secondary}
    />
);

interface IProps {
    item: PricesSearchRowItem;
    /** `getOptionProps(item.id)`. */
    optionProps: ISearchComboboxOptionDomProps;
    mode: SearchComboboxMode;
    /** Replaces the metrics column (the nearby spinner). */
    meta?: ReactNode;
    /** Recent rows: "×" removes the entry. */
    onRemove?: () => void;
    /** The subtitle wraps instead of ending in an ellipsis - a message that has to be read whole. */
    isSubtitleWrapped?: boolean;
    dataTestId?: string;
}

/**
 * One suggestion row: type or history tile, highlighted title, "Typ · region", price and transactions, ↵ on
 * desktop. An address without data is muted by `aria-disabled` from the combobox and says "brak danych" for the price.
 */
export const PricesSearchRow = (props: IProps) => {
    const {item, optionProps, mode, meta, onRemove, isSubtitleWrapped, dataTestId = PRICES_SEARCH_TEST_IDS.OPTION} = props;
    const isRecent = "recentId" in item && item.recentId !== undefined;
    const {primary, primaryHint, secondary} = getPricesSearchMetricLines(item.metrics);
    const isUnavailable = item.kind === "address" && item.isUnavailable === true;
    const metrics = isUnavailable ? (
        <PricesSearchUnavailableMetrics metrics={item.metrics} />
    ) : primary || secondary ? (
        <SearchComboboxMetrics primary={primary} primaryHint={primaryHint} secondary={secondary} />
    ) : undefined;

    return (
        <SearchComboboxOption
            {...optionProps}
            icon={<PricesSearchTypeIcon kind={isRecent ? "recent" : item.kind} />}
            title={
                <>
                    <HighlightedText text={item.title} ranges={item.highlightRanges} />
                    {item.kind === "street" ? (
                        <span className={srOnlyStyle}>
                            {item.isExpanded ? PRICES_SEARCH_COPY.streetState.expanded : PRICES_SEARCH_COPY.streetState.collapsed}
                        </span>
                    ) : null}
                </>
            }
            subtitle={item.subtitle}
            isSubtitleWrapped={isSubtitleWrapped}
            meta={meta ?? metrics}
            showKbd={mode === "dropdown"}
            {...(onRemove ? {onRemove, removeLabel: PRICES_SEARCH_COPY.removeRecent(item.title), removeDataTestId: PRICES_SEARCH_TEST_IDS.RECENT_REMOVE} : {})}
            dataTestId={dataTestId}
        />
    );
};

const srOnlyStyle = css({
    position: "absolute",
    width: "1px",
    height: "1px",
    padding: "0",
    margin: "-1px",
    overflow: "hidden",
    clip: "rect(0, 0, 0, 0)",
    whiteSpace: "nowrap",
    borderWidth: "0"
});
